import { DataListItemDirective, DataListDetailDirective } from '../../../shared/ui/data-list/data-list-item.directive';
import { WorkspaceViewDirective } from '../../../components/workspace-view/workspace-view.directive';
import { DataListComponent } from '../../../shared/ui/data-list/data-list.component';
import { DataPage, DataQuery } from '../../../models/data-source';
import { CommonModule } from '@angular/common';
import { Component, ViewChild } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { BrIconComponent } from '../../../shared/ui/icon/icon.component';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router, RouterModule } from '@angular/router';
import { EMPTY, Observable } from 'rxjs';
import { finalize, map, switchMap } from 'rxjs/operators';
import { DataTableColumn, DataTableComponent } from '../../../shared/ui/data-table/data-table.component';
import { DataTableCellDirective } from '../../../shared/ui/data-table/data-table-cell.directive';
import { ConfirmationDialogService } from '../../../shared/ui/confirmation-dialog/confirmation-dialog.service';
import {
  dynamicApiErrorMessage,
  DynamicFrameworkApiService,
} from './dynamic-framework-api.service';
import {
  DynamicWorkflowDefinition,
  DynamicWorkflowSummary,
  DynamicWorkflowWrite,
} from './dynamic-framework.models';
import {
  LeadsV2PublishWorkflowDialogComponent,
  PublishWorkflowDialogData,
  PublishWorkflowDialogResult,
} from './leads-v2-publish-workflow-dialog.component';
import { WorkflowIssue } from './dynamic-workflow-validation';

@Component({
  selector: 'app-leads-v2-page',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    MatButtonModule,
    BrIconComponent,
    MatTooltipModule,
    DataTableComponent, DataListComponent, DataListItemDirective, DataListDetailDirective, WorkspaceViewDirective,
    DataTableCellDirective,
  ],
  templateUrl: './leads-v2-page.component.html',
  styleUrls: ['./leads-v2-page.component.css'],
})
export class LeadsV2PageComponent {
  @ViewChild('compactList') compactList?: DataListComponent<DynamicWorkflowSummary>;
  collectionSearch = '';

  @ViewChild(DataTableComponent)
  table?: DataTableComponent<DynamicWorkflowSummary>;

  readonly columns: DataTableColumn[] = [
    { id: 'label', label: 'Name' },
    { id: 'status', label: 'Status' },
    { id: 'createdAt', label: 'Created', align: 'end' },
    { id: 'updatedAt', label: 'Updated', align: 'end' },
  ];

  readonly workflowRowId = (workflow: DynamicWorkflowSummary): string => workflow.key;
  readonly statusChangingKeys = new Set<string>();

  constructor(
    private readonly api: DynamicFrameworkApiService,
    private readonly confirmation: ConfirmationDialogService,
    private readonly dialog: MatDialog,
    private readonly router: Router,
    private readonly snackBar: MatSnackBar,
  ) {}

  fetch = (query: DataQuery): Observable<DataPage<DynamicWorkflowSummary>> =>
    this.api.listWorkflows({
      search: query.search || undefined,
      sort: query.sort || undefined,
      desc: query.desc,
      page: query.page,
      pageSize: query.pageSize,
    });

  createWorkflow(): void {
    this.router.navigate(['/smart-workflows/new']);
  }

  editWorkflow(workflow: DynamicWorkflowSummary): void {
    this.router.navigate(['/smart-workflows', workflow.key, 'edit']);
  }

  publishWorkflow(workflow: DynamicWorkflowSummary): void {
    if (workflow.status !== 'draft' || this.statusChangingKeys.has(workflow.key)) return;
    this.statusChangingKeys.add(workflow.key);

    this.api.getWorkflow(workflow.key).pipe(
      switchMap(definition => this.api.validateWorkflow(this.toWrite(definition)).pipe(
        map(validation => ({
          definition,
          issues: validation.issues.map(issue => ({
            severity: 'error' as const,
            message: issue.message,
            nodeKey: issue.nodeKey ?? undefined,
          })),
        })),
      )),
      switchMap(({ definition, issues }) => this.openPublishPreview(definition, issues).pipe(
        map(result => ({ definition, result })),
      )),
      switchMap(({ definition, result }) => {
        if (!result) return EMPTY;
        if (result.action === 'edit') {
          this.editWorkflow(workflow);
          return EMPTY;
        }
        return this.api.publishWorkflow(
          workflow.key,
          definition.updatedAt,
          result.startNodeKey,
        );
      }),
      finalize(() => this.statusChangingKeys.delete(workflow.key)),
    ).subscribe({
      next: () => {
        this.snackBar.open('Workflow published.', undefined, { duration: 2500 });
        this.table?.refresh();
        this.compactList?.reload();
      },
      error: error => this.snackBar.open(
        dynamicApiErrorMessage(error, 'Could not publish the workflow.'),
        'OK',
        { duration: 6000 },
      ),
    });
  }

  unpublishWorkflow(workflow: DynamicWorkflowSummary): void {
    if (workflow.status !== 'published' || this.statusChangingKeys.has(workflow.key)) return;
    this.statusChangingKeys.add(workflow.key);
    this.confirmation.confirm({
      title: 'Unpublish workflow?',
      message: 'This returns the workflow to Draft so it can be edited. '
        + 'A workflow with data cannot be unpublished.',
      confirmLabel: 'Unpublish',
    }).pipe(
      switchMap(confirmed => confirmed
        ? this.api.unpublishWorkflow(workflow.key, workflow.updatedAt)
        : EMPTY),
      finalize(() => this.statusChangingKeys.delete(workflow.key)),
    ).subscribe({
      next: () => {
        this.snackBar.open('Workflow returned to Draft.', undefined, { duration: 2500 });
        this.table?.refresh();
        this.compactList?.reload();
      },
      error: error => this.snackBar.open(
        dynamicApiErrorMessage(error, 'Could not unpublish the workflow.'),
        'OK',
        { duration: 6000 },
      ),
    });
  }

  private openPublishPreview(
    definition: DynamicWorkflowDefinition,
    issues: WorkflowIssue[],
  ): Observable<PublishWorkflowDialogResult | undefined> {
    return this.dialog.open<
      LeadsV2PublishWorkflowDialogComponent,
      PublishWorkflowDialogData,
      PublishWorkflowDialogResult
    >(LeadsV2PublishWorkflowDialogComponent, {
      data: {
        label: definition.label,
        nodes: definition.nodes,
        transitions: definition.transitions,
        issues,
        canEdit: true,
      },
      width: '820px',
      maxWidth: 'calc(100vw - 32px)',
      autoFocus: 'select',
    }).afterClosed();
  }

  private toWrite(definition: DynamicWorkflowDefinition): DynamicWorkflowWrite {
    return {
      label: definition.label,
      expectedUpdatedAt: definition.updatedAt,
      nodes: definition.nodes,
      transitions: definition.transitions,
    };
  }

  deleteWorkflow(workflow: DynamicWorkflowSummary): void {
    this.confirmation.confirm({
      title: 'Delete workflow?',
      message: `“${workflow.label}” will be permanently deleted.`,
      confirmLabel: 'Delete',
      danger: true,
    }).pipe(
      switchMap(confirmed => confirmed
        ? this.api.deleteWorkflow(workflow.key, workflow.updatedAt)
        : EMPTY),
    ).subscribe({
      next: () => {
        this.snackBar.open('Workflow deleted.', undefined, { duration: 2500 });
        this.table?.refresh();
        this.compactList?.reload();
      },
      error: error => this.snackBar.open(
        dynamicApiErrorMessage(error, 'Could not delete the workflow.'),
        'OK',
        { duration: 6000 },
      ),
    });
  }
}
